import type { OfferCancel, OfferCreate, Payment, Transaction } from 'xrpl/dist/npm'
import { OfferCreateFlags, PaymentFlags } from 'xrpl/dist/npm'
import { fromXrplAmount, toXrplAmount, toXrplAsset } from './amounts'
import { getXrplClient } from './client'
import { XrplAmount, XrplCurrency, XrplSubmitResult } from './types'

type XrplSignAndSubmit = (tx: Transaction) => Promise<XrplSubmitResult>

export type XrplOpenOrder = {
  sequence: number
  takerGets: XrplAmount
  takerPays: XrplAmount
  expiration?: number
  quality?: string
}

export type XrplOrderBookLevel = {
  price: number
  amount: number
  total: number
}

export type XrplOrderBook = {
  bids: XrplOrderBookLevel[]
  asks: XrplOrderBookLevel[]
}

const normalizeAmountValue = (currency: XrplCurrency, value: string, label: string) => {
  const numeric = Number(value)
  if (!Number.isFinite(numeric) || numeric <= 0) {
    throw new Error(`Invalid ${label} amount`)
  }
  const decimals = currency.isNative || currency.currency === 'XRP' ? 6 : Math.min(Math.max(currency.decimals ?? 15, 0), 15)
  const fixed = numeric.toFixed(decimals)
  const trimmed = fixed.replace(/\.?0+$/, '')
  if (!trimmed || Number(trimmed) <= 0) {
    throw new Error(`Invalid ${label} amount`)
  }
  return trimmed
}

export const swapExactIn = async ({
  account,
  from,
  to,
  amountIn,
  expectedOut,
  minOut,
  paths,
  signAndSubmit,
}: {
  account: string
  from: XrplCurrency
  to: XrplCurrency
  amountIn: string
  expectedOut: string
  minOut: string
  paths?: Payment['Paths']
  signAndSubmit: XrplSignAndSubmit
}) => {
  if (from.symbol === to.symbol) {
    throw new Error('Input and output tokens must be different')
  }

  const normalizedIn = normalizeAmountValue(from, amountIn, 'input')
  const normalizedOut = normalizeAmountValue(to, expectedOut, 'output')
  const numericMinOut = Number(minOut)
  const normalizedMin = Number.isFinite(numericMinOut) && numericMinOut > 0
    ? normalizeAmountValue(to, minOut, 'minimum output')
    : null

  const tx: Payment = {
    TransactionType: 'Payment',
    Account: account,
    Destination: account,
    Amount: toXrplAmount(to, normalizedOut),
    SendMax: toXrplAmount(from, normalizedIn),
    Flags: PaymentFlags.tfPartialPayment,
  }

  if (normalizedMin) {
    tx.DeliverMin = toXrplAmount(to, normalizedMin)
  }

  if (paths && paths.length) {
    tx.Paths = paths
  }

  return signAndSubmit(tx)
}

export const quotePathSwap = async ({
  account,
  from,
  to,
  amountIn,
}: {
  account: string
  from: XrplCurrency
  to: XrplCurrency
  amountIn: string
}) => {
  if (!account) {
    throw new Error('Missing account for pathfinding')
  }

  const client = await getXrplClient()
  const destinationAmount = toXrplAmount(to, '1')

  const response = await client.request({
    command: 'ripple_path_find',
    source_account: account,
    destination_account: account,
    destination_amount: destinationAmount,
    source_currencies: [toXrplAsset(from)],
  } as any)

  const alternatives = response.result?.alternatives ?? []
  const best = alternatives[0]
  const sourceAmount = best?.source_amount
  const sourceValue = sourceAmount ? Number(fromXrplAmount(sourceAmount)) : 0
  const inputValue = Number(amountIn || '0')
  const amountOut = sourceValue > 0 && inputValue > 0 ? (inputValue / sourceValue).toString() : '0'
  const paths = best?.paths_computed ?? []

  return { amountOut, paths }
}

export const quoteBookSwap = async ({
  account,
  from,
  to,
  amountIn,
}: {
  account?: string | null
  from: XrplCurrency
  to: XrplCurrency
  amountIn: string
}) => {
  const client = await getXrplClient()
  const takerGets = toXrplAsset(to)
  const takerPays = toXrplAsset(from)
  const response = await client.request({
    command: 'book_offers',
    taker_gets: takerGets,
    taker_pays: takerPays,
    taker: account || undefined,
    limit: 20,
  } as any)

  const offers = response.result?.offers ?? []
  let remaining = Number(amountIn || '0')
  let totalOut = 0

  // eslint-disable-next-line no-restricted-syntax
  for (const offer of offers) {
    if (remaining <= 0) break
    const paysAmount =
      offer?.taker_pays_funded ??
      offer?.TakerPaysFunded ??
      offer?.taker_pays ??
      offer?.TakerPays
    const getsAmount =
      offer?.taker_gets_funded ??
      offer?.TakerGetsFunded ??
      offer?.taker_gets ??
      offer?.TakerGets
    const pays = Number(fromXrplAmount(paysAmount))
    const gets = Number(fromXrplAmount(getsAmount))
    // eslint-disable-next-line no-continue
    if (!pays || !gets) continue
    const take = Math.min(remaining, pays)
    const ratio = take / pays
    totalOut += gets * ratio
    remaining -= take
  }

  return { amountOut: totalOut > 0 ? totalOut.toString() : '0' }
}

export const swapOrderBook = async ({
  account,
  from,
  to,
  amountIn,
  expectedOut,
  minOut,
  signAndSubmit,
}: {
  account: string
  from: XrplCurrency
  to: XrplCurrency
  amountIn: string
  expectedOut: string
  minOut: string
  signAndSubmit: XrplSignAndSubmit
}) => {
  if (from.symbol === to.symbol) {
    throw new Error('Input and output tokens must be different')
  }

  const normalizedIn = normalizeAmountValue(from, amountIn, 'input')
  const numericMinOut = Number(minOut)
  const normalizedOut =
    Number.isFinite(numericMinOut) && numericMinOut > 0
      ? normalizeAmountValue(to, minOut, 'minimum output')
      : normalizeAmountValue(to, expectedOut, 'output')

  const tx: OfferCreate = {
    TransactionType: 'OfferCreate',
    Account: account,
    TakerGets: toXrplAmount(from, normalizedIn),
    TakerPays: toXrplAmount(to, normalizedOut),
    Flags: OfferCreateFlags.tfImmediateOrCancel + OfferCreateFlags.tfSell,
  }

  return signAndSubmit(tx)
}

export const createLimitOrder = async ({
  account,
  from,
  to,
  amountIn,
  amountOut,
  expirationMinutes,
  signAndSubmit,
}: {
  account: string
  from: XrplCurrency
  to: XrplCurrency
  amountIn: string
  amountOut: string
  expirationMinutes?: number
  signAndSubmit: XrplSignAndSubmit
}) => {
  if (from.symbol === to.symbol) {
    throw new Error('Input and output tokens must be different')
  }

  const numericIn = Number(amountIn)
  const numericOut = Number(amountOut)
  if (!Number.isFinite(numericIn) || numericIn <= 0) {
    throw new Error('Enter an input amount')
  }
  if (!Number.isFinite(numericOut) || numericOut <= 0) {
    throw new Error('Enter a valid limit price')
  }

  const normalizedIn = normalizeAmountValue(from, amountIn, 'input')
  const normalizedOut = normalizeAmountValue(to, amountOut, 'output')

  const tx: OfferCreate = {
    TransactionType: 'OfferCreate',
    Account: account,
    TakerGets: toXrplAmount(from, normalizedIn),
    TakerPays: toXrplAmount(to, normalizedOut),
  }

  if (expirationMinutes && expirationMinutes > 0) {
    const rippleEpochOffset = 946684800
    const expiresAt = Math.floor(Date.now() / 1000) - rippleEpochOffset + Math.round(expirationMinutes * 60)
    tx.Expiration = expiresAt
  }

  return signAndSubmit(tx)
}

export const fetchOrderBook = async ({
  base,
  quote,
  limit = 20,
  account,
}: {
  base: XrplCurrency
  quote: XrplCurrency
  limit?: number
  account?: string | null
}): Promise<XrplOrderBook> => {
  const client = await getXrplClient()
  const baseAsset = toXrplAsset(base)
  const quoteAsset = toXrplAsset(quote)

  const parseOffers = (offers: any[], isAsk: boolean) => {
    return offers
      .map((offer) => {
        const gets =
          offer?.taker_gets_funded ??
          offer?.TakerGetsFunded ??
          offer?.taker_gets ??
          offer?.TakerGets
        const pays =
          offer?.taker_pays_funded ??
          offer?.TakerPaysFunded ??
          offer?.taker_pays ??
          offer?.TakerPays
        const getsValue = Number(fromXrplAmount(gets))
        const paysValue = Number(fromXrplAmount(pays))
        if (!getsValue || !paysValue) return null
        const price = isAsk ? paysValue / getsValue : getsValue / paysValue
        const amount = isAsk ? getsValue : paysValue
        const total = isAsk ? paysValue : getsValue
        if (!Number.isFinite(price) || !Number.isFinite(amount) || !Number.isFinite(total)) return null
        return { price, amount, total }
      })
      .filter(Boolean) as XrplOrderBookLevel[]
  }

  const [asksResponse, bidsResponse] = await Promise.all([
    client.request({
      command: 'book_offers',
      taker_gets: baseAsset,
      taker_pays: quoteAsset,
      taker: account || undefined,
      limit,
    } as any),
    client.request({
      command: 'book_offers',
      taker_gets: quoteAsset,
      taker_pays: baseAsset,
      taker: account || undefined,
      limit,
    } as any),
  ])

  const asks = parseOffers(asksResponse.result?.offers ?? [], true)
  const bids = parseOffers(bidsResponse.result?.offers ?? [], false)

  return { asks, bids }
}

export const fetchOpenOrders = async ({
  account,
  limit = 50,
}: {
  account: string
  limit?: number
}): Promise<XrplOpenOrder[]> => {
  const client = await getXrplClient()
  const response = await client.request({
    command: 'account_offers',
    account,
    limit,
  } as any)

  const offers = response.result?.offers ?? []
  return offers
    .map((offer: any) => ({
      sequence: offer.seq ?? offer.Sequence ?? offer.sequence ?? 0,
      takerGets: offer.taker_gets ?? offer.TakerGets,
      takerPays: offer.taker_pays ?? offer.TakerPays,
      expiration: offer.expiration ?? offer.Expiration,
      quality: offer.quality ?? offer.Quality,
    }))
    .filter((offer: XrplOpenOrder) => offer.sequence && offer.takerGets && offer.takerPays)
}

export const cancelLimitOrder = async ({
  account,
  offerSequence,
  signAndSubmit,
}: {
  account: string
  offerSequence: number
  signAndSubmit: XrplSignAndSubmit
}) => {
  if (!offerSequence) {
    throw new Error('Missing offer sequence')
  }

  const tx: OfferCancel = {
    TransactionType: 'OfferCancel',
    Account: account,
    OfferSequence: offerSequence,
  }

  return signAndSubmit(tx)
}
